import { useAuth } from './useAuth';
import { getTierConfig, hasFeature, getLimit, isWithinLimit } from '@/config/tiers';
import type { UserTier } from '@/types/country';
import type { TierConfig, TierFeatures, TierLimits } from '@/config/tiers';

interface UseTierReturn {
  tier: UserTier;
  config: TierConfig;
  isLoading: boolean;
  isFree: boolean;
  isLite: boolean;
  isPro: boolean;
  hasFeature: (feature: keyof TierFeatures) => boolean;
  getLimit: (limit: keyof TierLimits) => number;
  isWithinLimit: (limit: keyof TierLimits, currentUsage: number) => boolean;
  canUpgrade: boolean;
}

/**
 * Hook for tier-based feature access and limits
 * 
 * Provides feature flags and usage limits based on user's subscription tier
 */
export function useTier(): UseTierReturn {
  const { user, isLoading: authIsLoading } = useAuth();
  const tier = (user?.profile?.tier as UserTier) ?? 'free';
  const config = getTierConfig(tier);
  
  // Consider loading if auth is loading OR profile/tier not yet loaded
  const isLoading = authIsLoading || !user?.profile?.tier;

  return {
    tier,
    config,
    isLoading,
    isFree: tier === 'free',
    isLite: tier === 'lite',
    isPro: tier === 'pro',
    hasFeature: (feature: keyof TierFeatures) => hasFeature(tier, feature),
    getLimit: (limit: keyof TierLimits) => getLimit(tier, limit),
    isWithinLimit: (limit: keyof TierLimits, currentUsage: number) =>
      isWithinLimit(tier, limit, currentUsage),
    canUpgrade: tier !== 'pro',
  };
}
